import { ModalFactory, VdToastNotificationService } from '@vend/vend.ui'
import { VdFeatureManager } from '@vend/vend.js'
import angular from 'angular'

import selectRegisterTemplate from '../../templates/register/select-register.html'
import selectRegisterReactTemplate from '../../templates/register/select-register-react.html'
import openRegisterTemplate from '../components/register/open-register-continue-sale/open-register-continue-sale.html'
import { STATES } from '../../../lib/routes'
import { $q, $state } from '../ng-compat/imports'
import { RegisterManager } from '../managers/register-manager'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { RegisterClosureManager } from '../managers/register-closure-manager'

const UNSYNCED_SALES_COPY = `Before you can switch registers, it’s important to have all your sales synced to our
  servers, otherwise your sales will be deleted and cannot be recovered.`

function registerModals(
  Modal: ModalFactory,
  Confirm: $TSFixMe,
  registerManager: RegisterManager,
  saleSyncManager: SaleSyncManager,
  wrDebugTools: $TSFixMe,
  vdToastNotificationService: VdToastNotificationService,
  vdFeatureManager: VdFeatureManager,
  registerClosureManager: RegisterClosureManager
) {
  wrDebugTools.register('registerModals')

  /**
   * Provides register related modals e.g. to allow the user to select a register.
   *
   * @class RegisterModals
   * @static
   */
  return {
    /**
     * Open the select register lightbox.
     *
     * @method selectRegister
     *
     * @param [dismissible] {Boolean}
     *        True if the modal is dismissible, false otherwise.
     *
     * @return {Promise} The modal instance.
     */
    selectRegister(dismissible?: boolean) {
      const shouldLoadReactComponent = vdFeatureManager.get(
        'webregister_react_select_register'
      )

      return Modal({
        template: shouldLoadReactComponent
          ? selectRegisterReactTemplate
          : selectRegisterTemplate,
        controller: 'SelectRegisterController',
        controllerAs: '$ctrl',
        dismissible,
      })
    },

    /**
     * Open the "open register to continue sale" modal that prompts the user to open the current register before
     * they can continue loading the sale.
     *
     * @method openRegisterToContinueSaleIfNeeded
     *
     * @return {Promise} Resolved immediately if the register is already open, or once the user has actioned the
     *                   register to be opened.
     */
    openRegisterToContinueSaleIfNeeded() {
      if (registerManager.isActiveRegisterOpen()) {
        return $q.resolve()
      }

      if (!registerClosureManager.hasOpenClosePermission()) {
        return $q.reject()
      }

      return Modal({
        template: openRegisterTemplate,
        controller: 'OpenRegisterContinueSaleController',
        controllerAs: '$ctrl',
      }).then((modal: $TSFixMe) => modal.close)
    },

    /**
     * Open the "Register Closure Confirmation" modal that allows the user to confirm closing the register
     *
     * @method closeRegisterConfirmation
     *
     * @return {Promise} Resolved when the user has chosen 'Close Register'
     */
    closeRegisterConfirmation() {
      return Confirm({
        header: 'Are you sure you want to close this register?',
        content: `
        <p class="vd-p vd-mt3 vd-mb5">Your payment and sales totals will be finalised for this period.</p>`,
        confirm: {
          label: 'Close Register',
          category: 'no',
        },
      })
    },

    /**
     * Prompts the user to action their errored sales.
     *
     * @method actionErroredSales
     *
     * @return {Promise} Resolved when the user has chosen their course of action
     */
    actionErroredSales() {
      const failedToSyncSales =
        saleSyncManager.erroredSalesCount + saleSyncManager.attemptedSalesCount
      const pluraliseSale = failedToSyncSales > 1 ? 'sales' : 'sale'

      return Confirm({
        header: `You have ${failedToSyncSales} errored ${pluraliseSale}`,
        content: `<p class="vd-p">${UNSYNCED_SALES_COPY}</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: {
          label: `View errored ${pluraliseSale}`,
          resolve: () => {
            // @todo Replace with the View Errored Sales modal when built
            $state.go(STATES.STATUS)
          },
        },
      })
    },

    /**
     * Prompts the user to action their offline sales.
     *
     * @method actionOfflineSales
     *
     * @return {Promise} Resolved when the user has chosen their course of action
     */
    actionOfflineSales() {
      const uploadSales = saleSyncManager.uploadSalesCount
      const pluraliseSale = uploadSales > 1 ? 'sales' : 'sale'

      return Confirm({
        header: `You have ${uploadSales} offline ${pluraliseSale}`,
        content: `<p class="vd-p">${UNSYNCED_SALES_COPY}</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: {
          label: `Sync offline ${pluraliseSale}`,
          resolve: () => saleSyncManager.retryUnsyncedSales(),
        },
      }).then(() => {
        if (saleSyncManager.unsyncedSalesCount) {
          vdToastNotificationService.negative({
            message:
              'There was a problem syncing your offline sales. Please try again.',
          })
          return $q.reject()
        }
      })
    },
  }
}

export type RegisterModals = ReturnType<typeof registerModals>

/**
 * Provides register related modals e.g. to allow the user to select a register.
 *
 * @module registerModals
 */
angular.module('webregister.modals').factory('registerModals', registerModals)
